import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import { TextField } from "@/components/base/FormControls";
import { useAuth } from "@/context/AuthContext";
import { useAppSettings } from "@/context/AppSettingsContext";
import { supabase } from "@/lib/supabase";
import { BRAND_NAME, BRAND_TAGLINE } from "@/lib/constants";

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Dramatic%20dark%20studio%20background%20with%20stacked%20black%20performance%20tires%20and%20chrome%20alloy%20rims%2C%20red%20rim%20lighting%2C%20moody%20automotive%20workshop%20atmosphere%2C%20deep%20shadows%2C%20cinematic%20industrial%20composition%2C%20abstract%20stylized%20texture%2C%20high%20contrast%2C%20no%20text&width=1200&height=1600&seq=rkc-login-side-2026&orientation=portrait";

export default function LoginPage() {
  const { signInWithPassword } = useAuth();
  const { settings } = useAppSettings();

  const [checkingSetup, setCheckingSetup] = useState(true);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await supabase.rpc("admin_exists");
        if (active) setNeedsBootstrap(!data);
      } catch {
        if (active) setNeedsBootstrap(false);
      } finally {
        if (active) setCheckingSetup(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError("Ingresa tu correo y contraseña.");
      return;
    }
    setSubmitting(true);
    const { error: loginError } = await signInWithPassword(email, password);
    setSubmitting(false);
    if (loginError) setError("Credenciales incorrectas. Verifica e intenta de nuevo.");
  };

  const handleBootstrap = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!fullName.trim() || !email.trim() || password.length < 6) {
      setError("Completa nombre, correo y una contraseña de al menos 6 caracteres.");
      return;
    }
    setSubmitting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("rkc-bootstrap", {
        body: { email, password, full_name: fullName, last_name: lastName },
      });
      if (fnError) {
        setError("No se pudo crear el administrador. Intenta de nuevo.");
        setSubmitting(false);
        return;
      }
      const payload = data as { error?: string } | null;
      if (payload?.error) {
        setError(payload.error);
        setSubmitting(false);
        return;
      }
      const { error: loginError } = await signInWithPassword(email, password);
      setSubmitting(false);
      if (loginError) {
        setError("Administrador creado. Inicia sesión con tus credenciales.");
        setNeedsBootstrap(false);
      }
    } catch {
      setError("No se pudo crear el administrador. Intenta de nuevo.");
      setSubmitting(false);
    }
  };

  const companyName = settings.company_name || BRAND_NAME;

  return (
    <div className="flex min-h-screen w-full bg-background-50">
      {/* Brand panel */}
      <div className="relative hidden lg:flex lg:w-1/2 lg:flex-col lg:justify-end overflow-hidden bg-foreground-950">
        <img
          src={HERO_IMAGE}
          alt="Taller de neumáticos y repuestos RKC"
          title="Sistema RKC - Neumáticos y Servicios"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/35"></div>
        <div className="relative z-10 p-12 xl:p-16">
          <BrandLogo size={104} className="mb-7" />
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-white xl:text-5xl">
            {companyName}
          </h1>
          <p className="mt-3 text-sm font-semibold uppercase tracking-[0.22em] text-accent-400">
            {BRAND_TAGLINE}
          </p>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-white/75">
            Facturación, punto de venta, inventario, clientes y catálogo conectados en una sola
            plataforma para talleres de gomas y repuestos.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["POS rápido", "Inventario por sucursal", "Facturación fiscal"].map((feature) => (
              <span
                key={feature}
                className="rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-xs font-medium text-white/90"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex w-full flex-col justify-center px-5 py-10 sm:px-8 lg:w-1/2 lg:px-16">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8 flex flex-col items-center text-center lg:items-start lg:text-left">
            <BrandLogo size={78} className="mb-5 lg:hidden" />
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              {needsBootstrap ? "Configuración inicial" : "Acceso administrativo"}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-500">
              {needsBootstrap
                ? "Aún no existe un administrador. Crea el primero para comenzar a usar el sistema."
                : "Ingresa con tu correo y contraseña para administrar el negocio."}
            </p>
          </div>

          {checkingSetup ? (
            <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-6 text-sm text-foreground-500">
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-lg"></i>
              </span>
              Verificando configuración...
            </div>
          ) : (
            <form
              onSubmit={needsBootstrap ? handleBootstrap : handleLogin}
              className="flex flex-col gap-4"
            >
              {needsBootstrap ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <TextField
                    label="Nombre"
                    name="full_name"
                    value={fullName}
                    onChange={setFullName}
                    placeholder="Juan"
                    required
                    autoComplete="given-name"
                  />
                  <TextField
                    label="Apellido"
                    name="last_name"
                    value={lastName}
                    onChange={setLastName}
                    placeholder="Pérez"
                    autoComplete="family-name"
                  />
                </div>
              ) : null}

              <TextField
                label="Correo electrónico"
                name="email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="admin@rkc.com"
                required
                autoComplete="email"
                inputMode="email"
              />

              <div className="flex flex-col gap-1.5">
                <label htmlFor="password" className="text-xs font-semibold text-foreground-700">
                  Contraseña<span className="ml-0.5 text-primary-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="••••••••"
                    autoComplete={needsBootstrap ? "new-password" : "current-password"}
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 pr-11 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-foreground-400 hover:bg-background-100"
                  >
                    <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-base`}></i>
                  </button>
                </div>
              </div>

              {error ? (
                <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
                  <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
                    <i className="ri-error-warning-line text-base"></i>
                  </span>
                  <span className="flex-1 leading-snug">{error}</span>
                </div>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="mt-1 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? (
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                ) : (
                  <i className="ri-login-box-line text-base"></i>
                )}
                {needsBootstrap ? "Crear administrador" : "Iniciar sesión"}
              </button>
            </form>
          )}

          {!needsBootstrap && !checkingSetup ? (
            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                  <i className="ri-lock-line text-lg"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-900">¿Eres cajero?</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">
                    Accede rápidamente con tu PIN de 6 dígitos.
                  </p>
                </div>
              </div>
              <Link
                to="/pin"
                className="mt-3 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-200"
              >
                <i className="ri-arrow-right-line text-base"></i>
                Acceso con PIN
              </Link>
            </div>
          ) : null}

          <p className="mt-8 text-center text-xs text-foreground-400">
            {companyName} · {BRAND_TAGLINE}
          </p>
        </div>
      </div>
    </div>
  );
}